import { FunctionComponent, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { observer } from 'mobx-react-lite';
import { useStore } from '@hub/helpers/use-store';
import { canResendInvite, formatDate } from '@monorepo/client-common';
import { WithdrawPayload } from '@monorepo/client-types';
import { SystemIcon } from '@monorepo/nova-icons';
import { Button, Stack, Typography } from '@monorepo/nova-ui';
import { LoadingState, useLoading } from '@monorepo/react-hooks';

interface Props {
  form: UseFormReturn<WithdrawPayload>;
}

const RiseWithdraw: FunctionComponent<Props> = ({ form }) => {
  const {
    dataStore: { userStore, payoutStore },
  } = useStore();

  const { updateLoadingState, isLoading } = useLoading();
  const { t } = useTranslation(['errors', 'withdrawFunds']);

  const riseId = form.watch('metadata.riseId');
  const invitedAt = userStore.currentUser?.paymentData?.rise?.invitedAt;

  const canSendRiseInvite = canResendInvite(invitedAt);

  const handleSendInvite = async () => {
    try {
      if (!canSendRiseInvite) {
        return;
      }

      updateLoadingState(LoadingState.Loading);
      const response = await payoutStore.sendRiseInvitation();

      if (!response.success) {
        throw new Error('Failed sending rise invite');
      }

      userStore.updatePaymentData({
        rise: {
          invitedAt: response.invitedAt,
        },
      });

      message.success(t('withdrawFunds:rise_invitation_sent'));
    } catch (e) {
      console.error('Failed sending rise invite', e);
      message.error(t('errors:something_went_wrong'));
    } finally {
      updateLoadingState(LoadingState.Loaded);
    }
  };

  const renderSentResult = () => {
    if (!invitedAt) {
      return null;
    }

    return (
      <Stack gap={1} direction={'row'} alignItems="center">
        <SystemIcon iconName={'WarningCircle'} />
        <Typography>
          Last sent - {formatDate(invitedAt, { template: 'dateTimeShort' })}
        </Typography>
      </Stack>
    );
  };

  useEffect(() => {
    if (!riseId) {
      form.setError('metadata.riseId', {
        message: 'Rise id is required',
        type: 'manual',
      });
    }
    return () => {
      form.clearErrors('metadata.riseId');
    };
  }, [riseId]);

  if (riseId) {
    return (
      <Typography>{t('withdrawFunds:your_rise_id', { id: riseId })}</Typography>
    );
  }

  return (
    <Stack direction={'column'} gap={2}>
      <Button
        loading={isLoading}
        disabled={!canSendRiseInvite}
        onClick={handleSendInvite}
        fullWidth
        variant={'outlined'}
        endIcon={
          <SystemIcon
            iconName={'ArrowCircleRight'}
            color={canSendRiseInvite ? 'primary' : 'disabled'}
          />
        }
      >
        Send me rise invite
      </Button>
      <Stack justifyContent="space-between" direction={'row'} gap={10}>
        <Typography variant={'body1'}>
          You can send one invite every 3 hours
        </Typography>

        {renderSentResult()}
      </Stack>
    </Stack>
  );
};

export default observer(RiseWithdraw);
